/**
 * BlogPostPage Duck
 * Manages state for a single blog post page
 */

import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { apiBaseUrl } from '../../util/api';
import axios from 'axios';

// ================ Action Types ================ //

export const FETCH_BLOG_POST_REQUEST = 'app/BlogPostPage/FETCH_BLOG_POST_REQUEST';
export const FETCH_BLOG_POST_SUCCESS = 'app/BlogPostPage/FETCH_BLOG_POST_SUCCESS';
export const FETCH_BLOG_POST_ERROR = 'app/BlogPostPage/FETCH_BLOG_POST_ERROR';

// ================ Reducer ================ //

const initialState = {
  post: null,
  fetchInProgress: false,
  fetchError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_BLOG_POST_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_BLOG_POST_SUCCESS:
      return {
        ...state,
        post: payload,
        fetchInProgress: false,
        fetchError: null,
      };
    case FETCH_BLOG_POST_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };
    default:
      return state;
  }
}

// ================ Action Creators ================ //

export const fetchBlogPostRequest = () => ({
  type: FETCH_BLOG_POST_REQUEST,
});

export const fetchBlogPostSuccess = post => ({
  type: FETCH_BLOG_POST_SUCCESS,
  payload: post,
});

export const fetchBlogPostError = error => ({
  type: FETCH_BLOG_POST_ERROR,
  payload: error,
});

// ================ Thunks ================ //

// Helper function to make API calls that work in both browser and SSR
const blogApiRequest = (path, marketplaceRootURL) => {
  const baseUrl = apiBaseUrl(marketplaceRootURL);
  const url = `${baseUrl}${path}`;
  
  return axios.get(url, {
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
  }).then(response => response.data)
    .catch(error => {
      if (error.response) {
        const e = new Error(error.response.data?.message || `HTTP error! status: ${error.response.status}`);
        Object.assign(e, error.response.data || {});
        throw e;
      }
      throw error;
    });
};

export const fetchBlogPost = (slug, config = {}) => (dispatch, getState) => {
  const marketplaceRootURL = config?.marketplaceRootURL;
  dispatch(fetchBlogPostRequest());

  return blogApiRequest(`/api/blog/${slug}`, marketplaceRootURL)
    .then(data => {
      dispatch(fetchBlogPostSuccess(data));
      return data;
    })
    .catch(e => {
      log.error(e, 'fetch-blog-post-failed', { slug });
      const error = storableError(e);
      dispatch(fetchBlogPostError(error));
      throw error;
    });
};

// Nouvelle fonction pour récupérer une page par url_path
export const fetchPageByUrlPath = (urlPath, config = {}) => (dispatch, getState) => {
  const marketplaceRootURL = config?.marketplaceRootURL;
  dispatch(fetchBlogPostRequest());

  // NOTE: urlPath can contain slashes, so it must go through query param (not /:slug)
  return blogApiRequest(`/api/blog/page?url_path=${encodeURIComponent(urlPath)}`, marketplaceRootURL)
    .then(data => {
      dispatch(fetchBlogPostSuccess(data));
      return data;
    })
    .catch(e => {
      log.error(e, 'fetch-page-by-url-path-failed', { urlPath });
      const error = storableError(e);
      dispatch(fetchBlogPostError(error));
      throw error;
    });
};

// ================ Selectors ================ //

export const getBlogPost = state => state.BlogPostPage?.post || null;
export const getBlogPostFetchInProgress = state => state.BlogPostPage?.fetchInProgress || false;
export const getBlogPostFetchError = state => state.BlogPostPage?.fetchError || null;

// ================ Load Data ================ //

export const loadData = (params, search, config) => (dispatch, getState) => {
  const { slug, urlPath } = params; // urlPath si c'est une route à la racine
  
  if (urlPath) {
    // C'est une page à la racine, rechercher par url_path
    return dispatch(fetchPageByUrlPath(urlPath, config));
  } else {
    // C'est un article de blog, rechercher par slug
    return dispatch(fetchBlogPost(slug, config));
  }
};

